import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "fixed inset-0 z-100 overflow-y-auto" }
const _hoisted_2 = { class: "min-h-screen px-4 text-center" }
const _hoisted_3 = { class: "inline-block w-full max-w-lg mt-24 mb-8 p-6 overflow-hidden text-left transition-all transform bg-white shadow-xl rounded-lg min-h-50 overflow-visible" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_5 = { class: "list-disc ml-7" }
const _hoisted_6 = { class: "flex items-center gap-2 text-sm" }
const _hoisted_7 = {
  key: 0,
  class: "flex items-center gap-4 mt-4"
}
const _hoisted_8 = {
  key: 1,
  class: "mt-4"
}
const _hoisted_9 = { class: "flex mt-8 justify-center gap-4" }

import {
	TransitionChild,
	TransitionRoot,
	Dialog,
	DialogOverlay,
	DialogTitle
} from '@headlessui/vue';
import {
	useLoading,
	useToggle,
	SvgIcon,
	UiLoader,
	UiLine,
	UiButton
} from '@/ui-kit';
import { ref, computed } from 'vue';
import { sleep, substitute } from '@/utils';
import { useBilling } from '../composition';

enum Operation {
	remove = 'remove',
	add = 'add',
	increase = 'increase',
	decrease = 'decrease'
}

interface Item {
	id?: number;
	price: string;
	type: Operation;
	prod: string;
	name: string;
	quantity?: number;
	success?: boolean;
	error?: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PlanUpdateDialog',
  setup(__props, { expose }) {

const { isOpened, toggleOpened } = useToggle();
const { loading, toggleLoading } = useLoading(true);

const {
	editableBillingItems,
	currentSubItems,
	loadSubscription,
	setEditableBillingItems,
	removeProduct,
	decreasePlan,
	increasePlan,
	addProduct
} = useBilling();

function extractKeys(): Record<
	| 'productsToAdd'
	| 'productsToRemove'
	| 'productsToDecrease'
	| 'productsToIncrease',
	{
		product?: string;
		price?: string;
		id?: number;
		name?: string;
		quantity: number;
	}[]
> {
	const oldProducts = currentSubItems.value.map(
		(item) => item.stripe_product
	);
	const newProducts = editableBillingItems.value.map(
		(item) => item.stripe_product
	);

	const findOldProductData = (productId: string) => {
		const data = currentSubItems.value.find(
			(item) => item.stripe_product === productId
		);

		if (!data) {
			return {};
		}

		return {
			product: data.stripe_product,
			price: data.stripe_price,
			name: data.friendlyName,
			id: data.id
		};
	};

	const productsToRemove = currentSubItems.value
		.filter(
			(item) =>
				!newProducts.includes(item.stripe_product)
		)
		.map((product) => ({
			product: product.stripe_product,
			price: product.stripe_price,
			id: product.id,
			name: product.friendlyName,
			quantity: product.quantity
		}));

	const productsToAdd = editableBillingItems.value
		.filter(
			(item) =>
				!oldProducts.includes(item.stripe_product)
		)
		.map((product) => ({
			product: product.stripe_product,
			price: product.stripe_price,
			name: product.friendlyName,
			quantity: product.quantity
		}));

	const stayingProducts = oldProducts.filter(
		(product) =>
			!productsToRemove
				.map((item) => item.product)
				.includes(product)
	);

	const findOldStayingProductQuantity = (
		productId: string
	) =>
		currentSubItems.value.find(
			(item) => item.stripe_product === productId
		)?.quantity || 0;
	const findNewStayingProductQuantity = (
		productId: string
	) =>
		editableBillingItems.value.find(
			(item) => item.stripe_product === productId
		)?.quantity || 0;

	const productsToDecrease = stayingProducts
		.filter(
			(key) =>
				findNewStayingProductQuantity(key) <
				findOldStayingProductQuantity(key)
		)
		.map((key) => ({
			...findOldProductData(key),
			quantity:
				findOldStayingProductQuantity(key) -
				findNewStayingProductQuantity(key)
		}));

	const productsToIncrease = stayingProducts
		.filter(
			(key) =>
				findNewStayingProductQuantity(key) >
				findOldStayingProductQuantity(key)
		)
		.map((key) => ({
			...findOldProductData(key),
			quantity:
				findNewStayingProductQuantity(key) -
				findOldStayingProductQuantity(key)
		}));

	return {
		productsToRemove,
		productsToAdd,
		productsToDecrease,
		productsToIncrease
	};
}

const listOfUpdates = ref<Item[]>([]);

function getOperationByKey(
	key: keyof ReturnType<typeof extractKeys>
): Operation {
	switch (key) {
		case 'productsToAdd':
			return Operation.add;
		case 'productsToDecrease':
			return Operation.decrease;
		case 'productsToIncrease':
			return Operation.increase;
		case 'productsToRemove':
			return Operation.remove;
	}
}

function setListOfOperations(
	keys: ReturnType<typeof extractKeys>
) {
	listOfUpdates.value = [];
	const actionsOrder = [
		Operation.remove,
		Operation.decrease,
		Operation.increase,
		Operation.add
	];

	Object.entries(keys).forEach(([key, values]) => {
		if (!values.length) {
			return;
		}

		listOfUpdates.value = [
			...listOfUpdates.value,
			...values.map((val) => ({
				id: val.id,
				prod: val.product!,
				price: val.price!,
				quantity: val.quantity,
				name: val.name!,
				type: getOperationByKey(
					key as keyof ReturnType<
						typeof extractKeys
					>
				)
			}))
		];
	});

	listOfUpdates.value.sort(
		(left, right) =>
			actionsOrder.indexOf(left.type) -
			actionsOrder.indexOf(right.type)
	);
}

function openDialog() {
	const keys = extractKeys();
	setListOfOperations(keys);
	toggleLoading(false);
	toggleOpened(true);
}

function closeDialog() {
	toggleOpened(false);
}

expose({
	openDialog,
	closeDialog
});

function getTitle(item: Item) {
	const mapping = {
		get remove() {
			if (!loading.value) {
				return 'Remove %0';
			}

			if (!item.success && !item.error) {
				return 'Removing %0';
			}

			if (item.success) {
				return 'Removed %0';
			}

			return 'Failed to remove %0';
		},
		get add() {
			if (!loading.value) {
				return 'Add %1 items of %0';
			}

			if (!item.success && !item.error) {
				return 'Adding %1 items of %0';
			}

			if (item.success) {
				return 'Added %1 items of %0';
			}

			return 'Failed to add %1 items of %0';
		},
		get increase() {
			if (!loading.value) {
				return 'Increase %0 by %1';
			}

			if (!item.success && !item.error) {
				return 'Increasing %0 by %1';
			}

			if (item.success) {
				return 'Increased %0 by %1';
			}

			return 'Failed to increase %0 by %1';
		},
		get decrease() {
			if (!loading.value) {
				return 'Decrease %0 by %1';
			}

			if (!item.success && !item.error) {
				return 'Decreasing %0 by %1';
			}

			if (item.success) {
				return 'Decreased %0 by %1';
			}

			return 'Failed to decrease %0 by %1';
		}
	};

	return substitute(mapping[item.type], [
		item.name,
		item.quantity
	]);
}

const isEnded = computed(() => {
	return listOfUpdates.value.every(
		(item) => item.success || item.error
	);
});

function closeModal() {
	if (loading.value) {
		return;
	}

	closeDialog();
}

const hasErrors = computed(() => {
	return listOfUpdates.value.some((item) => item.error);
});

function makeOperation(item: Item) {
	switch (item.type) {
		case Operation.remove: {
			return removeProduct(item.id!)
				.then(() => {
					item.success = true;
				})
				.catch((error) => {
					item.error = error.message;
				});
		}
		case Operation.decrease: {
			return decreasePlan(item.id!, item.quantity!)
				.then(() => {
					item.success = true;
				})
				.catch((error) => {
					item.error = error.message;
				});
		}
		case Operation.increase: {
			return increasePlan(item.id!, item.quantity!)
				.then(() => {
					item.success = true;
				})
				.catch((error) => {
					item.error = error.message;
				});
		}
		case Operation.add: {
			return addProduct(item.price, item.quantity!)
				.then(() => {
					item.success = true;
				})
				.catch((error) => {
					item.error = error.message;
				});
		}
	}
}

async function updatePlan() {
	toggleLoading(true);
	for (const operation of listOfUpdates.value) {
		await makeOperation(operation);
	}
	await sleep(2);
	await loadSubscription();
	setEditableBillingItems();
	toggleLoading(false);
}

return (_ctx: any,_cache: any) => {
  const _component_el_tooltip = _resolveComponent("el-tooltip")!

  return (_openBlock(), _createBlock(_unref(TransitionRoot), {
    appear: "",
    show: _unref(isOpened),
    as: "template"
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(Dialog), {
        as: "div",
        onClose: closeModal
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(TransitionChild), {
                as: "template",
                enter: "duration-150 ease-out",
                "enter-from": "opacity-0",
                "enter-to": "opacity-100",
                leave: "duration-100 ease-in",
                "leave-from": "opacity-100",
                "leave-to": "opacity-0"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(DialogOverlay), { class: "fixed inset-0 bg-black bg-opacity-70" })
                ]),
                _: 1
              }),
              _createVNode(_unref(TransitionChild), {
                as: "template",
                enter: "duration-150 ease-out",
                "enter-from": "opacity-0 scale-95",
                "enter-to": "opacity-100 scale-100",
                leave: "duration-100 ease-in",
                "leave-from": "opacity-100 scale-100",
                "leave-to": "opacity-0 scale-95"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_3, [
                    _createVNode(_unref(DialogTitle), {
                      as: "h3",
                      class: "text-lg leading-6 text-center"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Here is the list of updates to your subscription. "),
                        _hoisted_4,
                        _createTextVNode(" Make sure that everything is correct and click continue. ")
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(UiLine)),
                    _createElementVNode("ul", _hoisted_5, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(listOfUpdates.value, (item, i) => {
                        return (_openBlock(), _createElementBlock("li", { key: i }, [
                          _createElementVNode("div", _hoisted_6, [
                            _createTextVNode(_toDisplayString(getTitle(item)) + " ", 1),
                            (
												!item.success &&
												!item.error &&
												_unref(loading)
											)
                              ? (_openBlock(), _createBlock(_unref(UiLoader), { key: 0 }))
                              : (
												item.success
											)
                                ? (_openBlock(), _createBlock(_unref(SvgIcon), {
                                    key: 1,
                                    name: "check",
                                    class: "h-4 w-4 text-green-200"
                                  }))
                                : (
												item.error
											)
                                  ? (_openBlock(), _createBlock(_component_el_tooltip, {
                                      key: 2,
                                      "show-after": 
													0
												,
                                      "hide-after": 
													0
												,
                                      content: 
													item.error
												
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(SvgIcon), {
                                          name: "close",
                                          class: "h-4 w-4 text-red-400 outline-none"
                                        })
                                      ]),
                                      _: 2
                                    }, 1032, ["content"]))
                                  : _createCommentVNode("", true)
                          ])
                        ]))
                      }), 128))
                    ]),
                    (_unref(loading) && _unref(isEnded))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                          _createTextVNode(" Reloading subscription "),
                          _createVNode(_unref(UiLoader))
                        ]))
                      : (
									!_unref(loading) && _unref(isEnded)
								)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _createTextVNode(" Subscription updated "),
                            (_unref(hasErrors))
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _createTextVNode(" with some errors ")
                                ], 64))
                              : _createCommentVNode("", true)
                          ]))
                        : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_9, [
                      (
										!_unref(isEnded) || _unref(loading)
									)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createVNode(_unref(UiButton), {
                              theme: "secondary",
                              onClick: closeDialog,
                              disabled: _unref(loading)
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(" Cancel ")
                              ]),
                              _: 1
                            }, 8, ["disabled"]),
                            _createVNode(_unref(UiButton), {
                              onClick: updatePlan,
                              loading: _unref(loading)
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(" Confirm changes ")
                              ]),
                              _: 1
                            }, 8, ["loading"])
                          ], 64))
                        : (_openBlock(), _createBlock(_unref(UiButton), {
                            key: 1,
                            onClick: closeDialog
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(" Close ")
                            ]),
                            _: 1
                          }))
                    ])
                  ])
                ]),
                _: 1
              })
            ])
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["show"]))
}
}

})